import { useEffect, useState } from "react";

/**
 * Keeps the screen on while a document is being read.
 *
 * A sermon runs longer than any sensible auto-lock, and there is no worse
 * moment to be woken by a black screen than mid-sentence in front of a
 * congregation. The lock is dropped the moment the reading view is left.
 *
 * The browser drops the lock itself whenever the tab is hidden and does not
 * give it back, so it has to be re-taken on `visibilitychange` — otherwise
 * glancing at another app once costs you the lock for the rest of the sermon.
 *
 * Unsupported browsers get `supported: false` and no lock; nothing else
 * changes. Safari has had this since 16.4, which covers the iPad case.
 */
export function useWakeLock(enabled: boolean): { supported: boolean; active: boolean } {
  const supported = typeof navigator !== "undefined" && "wakeLock" in navigator;
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!supported || !enabled) return;

    let sentinel: WakeLockSentinel | null = null;
    let cancelled = false;

    const acquire = async () => {
      if (cancelled || document.visibilityState !== "visible") return;
      try {
        sentinel = await navigator.wakeLock.request("screen");
        if (cancelled) {
          void sentinel.release();
          sentinel = null;
          return;
        }
        setActive(true);
        sentinel.addEventListener("release", () => setActive(false));
      } catch {
        // Denied by the browser (low battery, permissions policy). Reading the
        // document is unaffected, so this is not worth surfacing.
        setActive(false);
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") void acquire();
    };

    void acquire();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      void sentinel?.release().catch(() => {});
      setActive(false);
    };
  }, [supported, enabled]);

  return { supported, active };
}
